import { useEffect, useState } from 'react';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Card, Button, Tag, Space, Typography, Alert, Table, Modal, App as AntApp,
  Descriptions, DatePicker, Select, Input,
} from 'antd';
import { ReloadOutlined, DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { API_URL } from '../config';
import { fmtDataBr } from '../utils/dataFmt';
import { carimbarBlob, baixarFotoCarimbada, nomeCarimbado } from '../utils/fotoCarimbada';

interface FotoServer {
  id: number;
  s3_key: string;
  nome_original: string | null;
  lat: number | null;
  lon: number | null;
  accuracy_m?: number | string | null;
  sem_geotag: boolean;
  capturado_em: string;
  url: string;
}

const { Title, Text } = Typography;

interface DadosEstruturados {
  atividade?: { descricao?: string; categoria?: string };
  local?: { cidade?: string; especifico?: string };
  produtividade?: { unidade?: string; quantidade?: number; observacao?: string; escopo_homogeneo?: boolean };
  // Uma entrada por medida do dia. O dia de cadastro rende km de rede E pontos — antes
  // só cabia um par {unidade, quantidade} e metade do dia se perdia.
  produtividade_itens?: Array<{ unidade: string; quantidade: number; observacao?: string | null }>;
  obices?: Array<{ categoria: string; descricao: string }>;
  clima?: { categoria?: string; observacao?: string };
  equipe_presente?: string[];
  equipamentos_usados?: string[];
  /** Nomes próprios/siglas que a IA escreveu e NÃO estão no relato (servidor confere). */
  termos_fora_do_relato?: string[];
  observacoes_extras?: string;
  campos_faltantes?: string[];
  confianca?: number;
}

interface Diario {
  id: number;
  contrato_id: string;
  contrato_numero: string;
  contrato_titulo: string;
  encarregado_email: string;
  data_diario: string;
  texto_original: string;
  dados_estruturados: DadosEstruturados | null;
  ai_confianca: number | null;
  status: string;
  submetido_em: string | null;
  telegram_enviado_em: string | null;
  created_at: string;
  /** Quem estava em campo, resolvido em nome pelo servidor (coluna equipe_emails). */
  equipe_nomes?: string[];
}

const STATUS_TAG: Record<string, { color: string; txt: string }> = {
  rascunho:  { color: 'default', txt: 'Rascunho' },
  completo:  { color: 'blue', txt: 'Pronto p/ envio' },
  submetido: { color: 'success', txt: 'Submetido' },
  aprovado:  { color: 'success', txt: 'Aprovado' },
  reprovado: { color: 'error', txt: 'Reprovado' },
};

async function getToken(): Promise<string> {
  const s = await fetchAuthSession();
  return s.tokens?.idToken?.toString() ?? '';
}

function DiarioCampoInner() {
  const { message } = AntApp.useApp();
  const [diarios, setDiarios] = useState<Diario[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroData, setFiltroData] = useState<Dayjs | null>(dayjs());
  const [filtroContrato, setFiltroContrato] = useState<string | null>(null);
  const [contratos, setContratos] = useState<Array<{ id: string; numero: string; titulo: string }>>([]);
  const [erro, setErro] = useState<string | null>(null);

  const [modalAberto, setModalAberto] = useState(false);
  const [diarioSelecionado, setDiarioSelecionado] = useState<Diario | null>(null);
  const [fotos, setFotos] = useState<FotoServer[]>([]);
  const [baixandoZip, setBaixandoZip] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.get(`${API_URL}/mobile/contratos`, { headers: { Authorization: token } });
        const raw: any = r.data;
        const data: any = raw?.body ? JSON.parse(raw.body) : raw;
        setContratos(data?.contratos || []);
      } catch { /* silencioso */ }
    })();
  }, []);

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroData, filtroContrato]);

  async function carregar() {
    setLoading(true);
    setErro(null);
    try {
      const token = await getToken();
      // escopo=todos: no sistema web quem tem operacional.diario_campo.listar
      // ve diarios de todos (no PWA, sem esse flag, cada um ve so os proprios).
      const params: Record<string, string> = { escopo: 'todos' };
      if (filtroData) params.data = filtroData.format('YYYY-MM-DD');
      if (filtroContrato) params.contrato_id = filtroContrato;
      const r = await axios.get<{ diarios: Diario[] }>(`${API_URL}/mobile/diarios`, {
        headers: { Authorization: token },
        params,
      });
      setDiarios(r.data.diarios || []);
    } catch (e: any) {
      setErro(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }

  async function abrirDetalhe(d: Diario) {
    setDiarioSelecionado(d);
    setModalAberto(true);
    setFotos([]);
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/mobile/diarios/${d.id}/fotos`, {
        headers: { Authorization: token },
      });
      const raw: any = r.data;
      const data: any = raw?.body ? JSON.parse(raw.body) : raw;
      setFotos(data?.fotos || []);
    } catch { setFotos([]); }
  }

  // comCarimbo: imprime data/hora + coordenadas (metadados do banco) em cada
  // foto NA HORA DO DOWNLOAD — a foto armazenada continua limpa.
  async function baixarTodasFotosZip(comCarimbo: boolean) {
    if (!diarioSelecionado || !fotos.length) return;
    setBaixandoZip(true);
    try {
      const zip = new JSZip();
      let ok = 0, falhas = 0;
      for (let i = 0; i < fotos.length; i++) {
        const f = fotos[i];
        try {
          const resp = await fetch(f.url, { mode: 'cors', cache: 'no-store' });
          if (!resp.ok) throw new Error('HTTP ' + resp.status);
          let blob = await resp.blob();
          const ext = (f.nome_original?.split('.').pop() || 'jpg').toLowerCase();
          let nome = f.nome_original || `foto-${String(i + 1).padStart(2, '0')}.${ext}`;
          if (comCarimbo) {
            try {
              blob = await carimbarBlob(blob, f);
              nome = nomeCarimbado(nome, `foto-${String(i + 1).padStart(2, '0')}`);
            } catch { /* carimbo falhou nessa foto — entra sem carimbo */ }
          }
          zip.file(nome, blob);
          ok++;
        } catch { falhas++; }
      }
      if (ok === 0) {
        message.error('Não consegui baixar nenhuma foto. Se você atualizou o sistema recentemente, dê Ctrl+Shift+R (ou limpe os dados do site) e tente de novo.');
        return;
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      const d = diarioSelecionado;
      const filename = `diario-${d.id}-${d.contrato_numero}-${String(d.data_diario).slice(0, 10)}${comCarimbo ? '-carimbadas' : ''}.zip`;
      saveAs(blob, filename);
      if (falhas > 0) message.warning(`${ok} foto(s) no zip; ${falhas} não baixaram.`);
    } catch (e: any) {
      message.error('Falha no zip: ' + e.message);
    } finally {
      setBaixandoZip(false);
    }
  }

  async function excluirDiarioSelecionado() {
    if (!diarioSelecionado) return;
    const d = diarioSelecionado;
    Modal.confirm({
      title: 'Excluir diário',
      width: 480,
      content: (
        <div>
          <Text style={{ display: 'block', marginBottom: 4 }}>
            Diário #{d.id} — {d.contrato_numero} — {fmtDataBr(d.data_diario)}
          </Text>
          <Text style={{ display: 'block', marginBottom: 8 }}>
            Encarregado: {d.encarregado_email}
          </Text>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>Motivo da exclusão *</Text>
          <Input.TextArea id="motivo-exclusao-diario" placeholder="Ex: lançamento duplicado, teste, etc." rows={3} />
        </div>
      ),
      okText: 'Excluir',
      okButtonProps: { danger: true, icon: <DeleteOutlined /> },
      cancelText: 'Cancelar',
      onOk: async () => {
        const motivo = (document.getElementById('motivo-exclusao-diario') as HTMLTextAreaElement)?.value?.trim() || '';
        if (motivo.length < 5) {
          message.error('Motivo precisa de pelo menos 5 caracteres');
          throw new Error('motivo invalido');
        }
        try {
          const token = await getToken();
          await axios.delete(`${API_URL}/mobile/diarios/${d.id}`, {
            headers: { Authorization: token },
            data: { motivo },
          });
          message.success('Diário excluído');
          setModalAberto(false);
          carregar();
        } catch (e: any) {
          message.error('Falha: ' + (e.response?.data?.error || e.message));
        }
      },
    });
  }

  const columns = [
    {
      title: 'Data', dataIndex: 'data_diario', width: 110,
      render: (v: string) => fmtDataBr(v),
    },
    {
      title: 'Contrato', key: 'contrato', width: 110,
      render: (_: any, d: Diario) => d.contrato_numero,
    },
    {
      title: 'Atividade', key: 'atividade',
      render: (_: any, d: Diario) => d.dados_estruturados?.atividade?.descricao || <Text type="secondary">—</Text>,
    },
    {
      title: 'Produção', key: 'prod', width: 140,
      render: (_: any, d: Diario) => {
        const itens = d.dados_estruturados?.produtividade_itens || [];
        if (itens.length) return itens.map((m) => `${m.quantidade} ${m.unidade}`).join(' · ');
        const p = d.dados_estruturados?.produtividade;
        return p?.quantidade != null ? `${p.quantidade} ${p.unidade || ''}` : '—';
      },
    },
    {
      title: 'Encarregado', dataIndex: 'encarregado_email',
      render: (v: string) => <Text style={{ fontSize: 12 }}>{v}</Text>,
    },
    {
      title: 'Status', dataIndex: 'status', width: 140,
      render: (s: string) => {
        const t = STATUS_TAG[s] || { color: 'default', txt: s };
        return <Tag color={t.color}>{t.txt}</Tag>;
      },
    },
    {
      title: '', key: 'acoes', width: 90,
      render: (_: any, d: Diario) => <Button size="small" type="link" onClick={() => abrirDetalhe(d)}>Ver</Button>,
    },
  ];

  return (
    <Card style={{ margin: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <Title level={3} style={{ margin: 0 }}>📝 Diário de Campo</Title>
          <Space wrap>
            <DatePicker value={filtroData} onChange={setFiltroData} format="DD/MM/YYYY" placeholder="filtrar por data" allowClear />
            <Select
              style={{ width: 280 }}
              value={filtroContrato}
              onChange={setFiltroContrato}
              options={contratos.map((c) => ({ label: `${c.numero} ${c.titulo}`, value: c.id }))}
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="filtrar por contrato"
            />
            <Button icon={<ReloadOutlined />} onClick={carregar} loading={loading}>Atualizar</Button>
          </Space>
        </div>

        {erro && <Alert type="error" message={erro} />}

        <Table
          rowKey="id"
          dataSource={diarios}
          columns={columns}
          loading={loading}
          pagination={{ pageSize: 50 }}
          locale={{ emptyText: 'Sem diários neste filtro' }}
        />
      </Space>

      <Modal
        open={modalAberto}
        onCancel={() => setModalAberto(false)}
        title={diarioSelecionado ? `Diário #${diarioSelecionado.id} — ${diarioSelecionado.contrato_numero}` : ''}
        width={720}
        footer={[
          <Button key="close" onClick={() => setModalAberto(false)}>Fechar</Button>,
          <Button key="delete" danger icon={<DeleteOutlined />} onClick={excluirDiarioSelecionado}>
            Excluir
          </Button>,
        ]}
      >
        {diarioSelecionado && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="Data">{fmtDataBr(diarioSelecionado.data_diario)}</Descriptions.Item>
              <Descriptions.Item label="Encarregado">{diarioSelecionado.encarregado_email}</Descriptions.Item>
              <Descriptions.Item label="Contrato" span={2}>{diarioSelecionado.contrato_numero} — {diarioSelecionado.contrato_titulo}</Descriptions.Item>
              <Descriptions.Item label="Status"><Tag color={STATUS_TAG[diarioSelecionado.status]?.color}>{STATUS_TAG[diarioSelecionado.status]?.txt || diarioSelecionado.status}</Tag></Descriptions.Item>
              <Descriptions.Item label="Telegram">{diarioSelecionado.telegram_enviado_em ? '✓ enviado' : '— pendente'}</Descriptions.Item>
            </Descriptions>

            {diarioSelecionado.dados_estruturados && (
              <Card size="small" title="Dados estruturados">
                <Descriptions size="small" column={1}>
                  {diarioSelecionado.dados_estruturados.atividade?.descricao && (
                    <Descriptions.Item label="Atividade">{diarioSelecionado.dados_estruturados.atividade.descricao}</Descriptions.Item>
                  )}
                  {diarioSelecionado.dados_estruturados.atividade?.categoria && (
                    <Descriptions.Item label="Categoria">{diarioSelecionado.dados_estruturados.atividade.categoria}</Descriptions.Item>
                  )}
                  {(diarioSelecionado.dados_estruturados.local?.cidade || diarioSelecionado.dados_estruturados.local?.especifico) && (
                    <Descriptions.Item label="Local">
                      {[diarioSelecionado.dados_estruturados.local?.cidade, diarioSelecionado.dados_estruturados.local?.especifico].filter(Boolean).join(' — ')}
                    </Descriptions.Item>
                  )}
                  {diarioSelecionado.dados_estruturados.produtividade?.quantidade != null && (
                    <Descriptions.Item label="Produção">
                      {diarioSelecionado.dados_estruturados.produtividade.quantidade} {diarioSelecionado.dados_estruturados.produtividade.unidade || ''}
                      {diarioSelecionado.dados_estruturados.produtividade.observacao && ` — ${diarioSelecionado.dados_estruturados.produtividade.observacao}`}
                    </Descriptions.Item>
                  )}
                  {diarioSelecionado.dados_estruturados.produtividade?.escopo_homogeneo === false
                    && !(diarioSelecionado.dados_estruturados.produtividade_itens?.length) && (
                    <Descriptions.Item label="Produção">
                      <Tag color="warning">sem produção medida</Tag>
                    </Descriptions.Item>
                  )}
                  {diarioSelecionado.dados_estruturados.clima?.categoria && (
                    <Descriptions.Item label="Clima">{diarioSelecionado.dados_estruturados.clima.categoria}{diarioSelecionado.dados_estruturados.clima.observacao ? ` — ${diarioSelecionado.dados_estruturados.clima.observacao}` : ''}</Descriptions.Item>
                  )}
                  {(diarioSelecionado.equipe_nomes?.length ?? 0) > 0 && (
                    <Descriptions.Item label="Equipe">{(diarioSelecionado.equipe_nomes || []).join(', ')}</Descriptions.Item>
                  )}
                  {(diarioSelecionado.dados_estruturados.equipe_presente?.length ?? 0) > 0 && (
                    <Descriptions.Item label="Citados no texto">{(diarioSelecionado.dados_estruturados.equipe_presente || []).join(', ')}</Descriptions.Item>
                  )}
                  {(diarioSelecionado.dados_estruturados.equipamentos_usados?.length ?? 0) > 0 && (
                    <Descriptions.Item label="Equipamentos">{(diarioSelecionado.dados_estruturados.equipamentos_usados || []).join(', ')}</Descriptions.Item>
                  )}
                  {(diarioSelecionado.dados_estruturados.termos_fora_do_relato?.length ?? 0) > 0 && (
                    <Descriptions.Item label="Palavras fora do relato">
                      <Tag color="warning">{(diarioSelecionado.dados_estruturados.termos_fora_do_relato || []).join(', ')}</Tag>
                      <span style={{ color: '#888' }}> — a IA escreveu e a pessoa não; conferir com o texto original</span>
                    </Descriptions.Item>
                  )}
                  {(diarioSelecionado.dados_estruturados.obices?.length ?? 0) > 0 && (
                    <Descriptions.Item label="Óbices">
                      <ul style={{ margin: 0, paddingLeft: 16 }}>
                        {(diarioSelecionado.dados_estruturados.obices || []).map((o, i) => (
                          <li key={i}><strong>{o.categoria}:</strong> {o.descricao}</li>
                        ))}
                      </ul>
                    </Descriptions.Item>
                  )}
                  {diarioSelecionado.dados_estruturados.observacoes_extras && (
                    <Descriptions.Item label="Observações">{diarioSelecionado.dados_estruturados.observacoes_extras}</Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            )}

            {diarioSelecionado.texto_original && (
              <Card size="small" title="Texto original">
                <Text italic style={{ whiteSpace: 'pre-wrap' }}>"{diarioSelecionado.texto_original}"</Text>
              </Card>
            )}

            <Card
              size="small"
              title={`📷 Fotos (${fotos.length})`}
              extra={fotos.length > 0 && (
                <Space size={4}>
                  <Button
                    size="small"
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={() => baixarTodasFotosZip(true)}
                    loading={baixandoZip}
                    title="Cada foto sai com data/hora e coordenadas impressas"
                  >
                    Baixar carimbadas
                  </Button>
                  <Button size="small" onClick={() => baixarTodasFotosZip(false)} loading={baixandoZip}>
                    sem carimbo
                  </Button>
                </Space>
              )}
            >
              {fotos.length === 0 ? (
                <Text type="secondary">Nenhuma foto anexada.</Text>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
                  {fotos.map((f) => (
                    <a key={f.id} href={f.url} target="_blank" rel="noreferrer" style={{ position: 'relative', display: 'block' }}>
                      <img src={f.url} alt={f.nome_original || ''} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 4, border: '1px solid #eee' }} />
                      {f.sem_geotag && (
                        <span style={{ position: 'absolute', top: 4, left: 4, background: '#f59e0b', color: 'white', fontSize: 9, padding: '1px 4px', borderRadius: 3, fontWeight: 600 }}>
                          sem GPS
                        </span>
                      )}
                      <span
                        title="Baixar com carimbo de data/hora e coordenadas"
                        onClick={(ev) => {
                          ev.preventDefault();
                          ev.stopPropagation();
                          baixarFotoCarimbada(f).catch((e) => message.error('Falha ao baixar: ' + (e as Error).message));
                        }}
                        style={{ position: 'absolute', bottom: 4, right: 4, background: 'rgba(0,0,0,0.6)', color: 'white', fontSize: 10, padding: '1px 5px', borderRadius: 3, fontWeight: 600, cursor: 'pointer' }}
                      >🕒⬇</span>
                    </a>
                  ))}
                </div>
              )}
            </Card>
          </Space>
        )}
      </Modal>
    </Card>
  );
}

export default function OperacionalDiarioCampo() {
  return <AntApp><DiarioCampoInner /></AntApp>;
}
